import { useDashboard } from '@/hooks/useDashboard';
import { DashboardActivity, DashboardUsage } from '@/types/dashboard.types';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  CheckCircle,
  CreditCard,
  Database,
  FolderOpen,
  Globe,
  Heart,
  Loader2,
  RefreshCw,
  Server,
  Users,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Stat Card Component
const StatCard = ({
  title,
  value,
  icon: Icon,
  color = 'primary',
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color?: 'primary' | 'success' | 'warning' | 'error';
  loading?: boolean;
}) => {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600',
    success: 'bg-success-50 text-success-600',
    warning: 'bg-warning-50 text-warning-600',
    error: 'bg-error-50 text-error-600',
  };

  return (
    <div className="group hover:border-primary-200 rounded-2xl border border-neutral-200 bg-white p-4 transition-all duration-200 hover:shadow-lg sm:p-6">
      <div className="flex items-center gap-4">
        <div
          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl transition-colors ${colorClasses[color]}`}
        >
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-500">{title}</p>
          {loading ? (
            <div className="mt-1 h-6 w-16 animate-pulse rounded bg-neutral-200"></div>
          ) : (
            <p className="mt-1 text-2xl font-bold text-neutral-900">{value}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Pricing Card Component (current pricing)
const PricingCard = ({ loading }: { loading?: boolean }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-32 rounded bg-neutral-200"></div>
          <div className="h-4 w-48 rounded bg-neutral-200"></div>
          <div className="h-4 w-24 rounded bg-neutral-200"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="group hover:border-primary-200 rounded-2xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:shadow-lg">
      <div className="flex items-start gap-4">
        <div className="bg-primary-50 group-hover:bg-primary-100 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl transition-colors">
          <CreditCard className="text-primary-600 h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-bold text-neutral-900">
            {t('dashboard.pricing.title', 'Pricing')}
          </h3>
          <div className="mt-2 space-y-2">
            <p className="text-sm text-neutral-700">
              {t(
                'dashboard.pricing.perFolder',
                '€1 excl. VAT per used folder each month.'
              )}
            </p>
            <p className="text-xs text-neutral-500">
              {t(
                'dashboard.pricing.details',
                'Billing is based on the number of folders used in your automations during the month.'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Health Score Component
const HealthScoreCard = ({
  score,
  loading,
}: {
  score: number;
  loading: boolean;
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-32 rounded bg-neutral-200"></div>
          <div className="h-16 w-16 rounded-full bg-neutral-200"></div>
        </div>
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'primary';
    if (score >= 50) return 'warning';
    return 'error';
  };

  const getScoreText = (score: number) => {
    if (score >= 90) return t('dashboard.healthScore.excellent');
    if (score >= 70) return t('dashboard.healthScore.good');
    if (score >= 50) return t('dashboard.healthScore.fair');
    return t('dashboard.healthScore.poor');
  };

  const color = getScoreColor(score);
  const colorClasses = {
    primary: 'text-primary-600 border-primary-200',
    success: 'text-success-600 border-success-200',
    warning: 'text-warning-600 border-warning-200',
    error: 'text-error-600 border-red-200',
  };

  return (
    <div className="group hover:border-primary-200 rounded-2xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:shadow-lg">
      <div className="flex items-center gap-4">
        <div
          className={`flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full border-4 ${colorClasses[color]}`}
        >
          <Heart className="h-8 w-8" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-bold text-neutral-900">
            {t('dashboard.healthScore.title')}
          </h3>
          <p className="text-3xl font-bold text-neutral-900">{score}%</p>
          <p className="text-sm text-neutral-600">{getScoreText(score)}</p>
        </div>
      </div>
    </div>
  );
};

// Recent Activity Component
const RecentActivityCard = ({
  activities,
  loading,
}: {
  activities: DashboardActivity[];
  loading: boolean;
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-32 rounded bg-neutral-200"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-neutral-200"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 rounded bg-neutral-200"></div>
                <div className="h-3 w-1/2 rounded bg-neutral-200"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'automation_success':
        return <CheckCircle className="text-success-600 h-5 w-5" />;
      case 'automation_failure':
        return <AlertTriangle className="text-error-600 h-5 w-5" />;
      case 'domain_sync':
        return <RefreshCw className="text-primary-600 h-5 w-5" />;
      default:
        return <Activity className="h-5 w-5 text-neutral-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="group hover:border-primary-200 rounded-2xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:shadow-lg">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-neutral-900">
          {t('dashboard.recentActivity.title')}
        </h3>
      </div>

      {activities.length === 0 ? (
        <div className="py-8 text-center">
          <Activity className="mx-auto h-12 w-12 text-neutral-400" />
          <p className="mt-2 text-sm text-neutral-500">
            {t('dashboard.recentActivity.noActivity')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities.slice(0, 5).map((activity, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-50">
                {getActivityIcon(activity.type)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-900">
                  {t(`dashboard.recentActivity.${activity.type}`) ||
                    activity.description}
                </p>
                <p className="text-sm text-neutral-600">
                  {activity.entityName}
                </p>
                <p className="text-xs text-neutral-500">
                  {formatDate(activity.timestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Usage Card Component
const UsageCard = ({
  usage,
  loading,
}: {
  usage: DashboardUsage;
  loading: boolean;
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-32 rounded bg-neutral-200"></div>
          <div className="space-y-3">
            <div className="h-4 w-full rounded bg-neutral-200"></div>
            <div className="h-4 w-full rounded bg-neutral-200"></div>
          </div>
        </div>
      </div>
    );
  }

  const isUnlimited = (max: number) => max >= 999999;

  return (
    <div className="group hover:border-primary-200 rounded-2xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:shadow-lg">
      <div className="mb-4 flex items-center gap-4">
        <div className="bg-primary-50 group-hover:bg-primary-100 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl transition-colors">
          <BarChart3 className="text-primary-600 h-6 w-6" />
        </div>
        <h3 className="text-lg font-bold text-neutral-900">
          {t('dashboard.usage.title')}
        </h3>
      </div>

      <div className="space-y-4">
        <div>
          <div className="mb-2 flex justify-between text-sm">
            <span className="font-medium text-neutral-700">
              {t('dashboard.usage.domains')}
            </span>
            <span className="text-neutral-900">
              {usage.currentDomains}{' '}
              {isUnlimited(usage.maxDomains)
                ? `/ ${t('dashboard.usage.unlimited')}`
                : `/ ${usage.maxDomains}`}
            </span>
          </div>
          {!isUnlimited(usage.maxDomains) && (
            <div className="h-2 rounded-full bg-neutral-200">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${usage.domainsUsagePercentage}%` }}
              ></div>
            </div>
          )}
        </div>

        <div>
          <div className="mb-2 flex justify-between text-sm">
            <span className="font-medium text-neutral-700">
              {t('dashboard.usage.automations')}
            </span>
            <span className="text-neutral-900">
              {usage.currentAutomations}{' '}
              {isUnlimited(usage.maxAutomations)
                ? `/ ${t('dashboard.usage.unlimited')}`
                : `/ ${usage.maxAutomations}`}
            </span>
          </div>
          {!isUnlimited(usage.maxAutomations) && (
            <div className="h-2 rounded-full bg-neutral-200">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${usage.automationsUsagePercentage}%` }}
              ></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
export function Dashboard() {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, error, refetch } = useDashboard();

  if (error) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <BarChart3 className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('dashboard.title')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('dashboard.subtitle')}
            </p>
          </div>
        </div>

        <div className="bg-error-50 rounded-2xl border border-red-200 p-8 text-center sm:p-12">
          <div className="bg-error-100 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full sm:h-16 sm:w-16">
            <AlertTriangle className="text-error-600 h-6 w-6 sm:h-8 sm:w-8" />
          </div>
          <h3 className="text-error-900 mb-2 text-lg font-semibold">
            {t('dashboard.error.title')}
          </h3>
          <p className="text-error-700 mx-auto mb-4 max-w-md text-sm sm:text-base">
            {t('dashboard.error.description')}
          </p>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="bg-error-600 hover:bg-error-700 inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold text-white transition-colors disabled:opacity-50"
          >
            {isFetching ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <RefreshCw className="h-5 w-5" />
            )}
            {t('dashboard.error.retry')}
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <BarChart3 className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('dashboard.title')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('dashboard.subtitle')}
            </p>
          </div>
        </div>

        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="bg-primary-600 hover:bg-primary-700 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold text-white transition-colors disabled:opacity-50 sm:w-auto"
        >
          {isFetching ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <RefreshCw className="h-5 w-5" />
          )}
          {t('dashboard.stats.refreshData')}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t('dashboard.domains.total')}
          value={data?.domains.total || 0}
          icon={Globe}
          color="primary"
          loading={isLoading}
        />
        <StatCard
          title={t('dashboard.folders.total')}
          value={data?.folders.total || 0}
          icon={FolderOpen}
          color="success"
          loading={isLoading}
        />
        <StatCard
          title={t('dashboard.automations.activeConfigurations')}
          value={data?.automations.activeConfigurations || 0}
          icon={Zap}
          color="warning"
          loading={isLoading}
        />
        <StatCard
          title={t('dashboard.notifications.unreadCount')}
          value={data?.notifications.unreadCount || 0}
          icon={Bell}
          color="error"
          loading={isLoading}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column */}
        <div className="space-y-6 lg:col-span-2">
          {/* Pricing Card */}
          <PricingCard loading={isLoading} />

          {/* Domains & Folders Stats */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border border-neutral-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <Server className="text-primary-600 h-6 w-6" />
                <h3 className="text-lg font-bold text-neutral-900">
                  {t('dashboard.domains.title')}
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    {t('dashboard.domains.connected')}
                  </span>
                  <span className="font-medium text-neutral-900">
                    {isLoading ? (
                      <div className="h-4 w-8 animate-pulse rounded bg-neutral-200"></div>
                    ) : (
                      data?.domains.connected || 0
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    {t('dashboard.domains.synced')}
                  </span>
                  <span className="font-medium text-neutral-900">
                    {isLoading ? (
                      <div className="h-4 w-8 animate-pulse rounded bg-neutral-200"></div>
                    ) : (
                      data?.domains.synced || 0
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    {t('dashboard.domains.errors')}
                  </span>
                  <span className="text-error-600 font-medium">
                    {isLoading ? (
                      <div className="h-4 w-8 animate-pulse rounded bg-neutral-200"></div>
                    ) : (
                      data?.domains.errors || 0
                    )}
                  </span>
                </div>
                {data?.domains.lastSyncDate && (
                  <div className="pt-2 text-xs text-neutral-500">
                    {t('dashboard.domains.lastSync')}:{' '}
                    {formatDate(data.domains.lastSyncDate)}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-neutral-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <Database className="text-success-600 h-6 w-6" />
                <h3 className="text-lg font-bold text-neutral-900">
                  {t('dashboard.automations.title')}
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    {t('dashboard.automations.executionsLast30Days')}
                  </span>
                  <span className="font-medium text-neutral-900">
                    {isLoading ? (
                      <div className="h-4 w-8 animate-pulse rounded bg-neutral-200"></div>
                    ) : (
                      data?.automations.executionsLast30Days || 0
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    {t('dashboard.automations.successRate')}
                  </span>
                  <span className="text-success-600 font-medium">
                    {isLoading ? (
                      <div className="h-4 w-8 animate-pulse rounded bg-neutral-200"></div>
                    ) : (
                      `${data?.automations.successRate || 0}%`
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    {t('dashboard.automations.failedExecutions')}
                  </span>
                  <span className="text-error-600 font-medium">
                    {isLoading ? (
                      <div className="h-4 w-8 animate-pulse rounded bg-neutral-200"></div>
                    ) : (
                      data?.automations.failedExecutions || 0
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <RecentActivityCard
            activities={data?.recentActivity || []}
            loading={isLoading}
          />
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Health Score */}
          <HealthScoreCard score={data?.healthScore || 0} loading={isLoading} />

          {/* Usage */}
          <UsageCard
            usage={
              data?.usage ?? {
                maxDomains: 0,
                currentDomains: 0,
                maxAutomations: 0,
                currentAutomations: 0,
                domainsUsagePercentage: 0,
                automationsUsagePercentage: 0,
              }
            }
            loading={isLoading}
          />

          {/* User Stats */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6">
            <div className="mb-4 flex items-center gap-3">
              <Users className="text-primary-600 h-6 w-6" />
              <h3 className="text-lg font-bold text-neutral-900">
                {t('dashboard.stats.memberSince')}
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">
                  {t('dashboard.stats.memberSince')}
                </span>
                <span className="font-medium text-neutral-900">
                  {isLoading ? (
                    <div className="h-4 w-16 animate-pulse rounded bg-neutral-200"></div>
                  ) : (
                    data?.memberSince && formatDate(data.memberSince)
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">
                  {t('dashboard.stats.lastLogin')}
                </span>
                <span className="font-medium text-neutral-900">
                  {isLoading ? (
                    <div className="h-4 w-16 animate-pulse rounded bg-neutral-200"></div>
                  ) : (
                    data?.lastLoginAt && formatDate(data.lastLoginAt)
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
